/* insightsFilterSlice.ts */

/* Redux Toolkit */
import {
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

/* Thunks */
import { fetchInsightsFilter } from "src/redux/thunks/insights-filter/insightsFilterThunks";
import { fetchMainConditions } from "src/redux/thunks/insights-filter/main-condition/mainConditionThunk";
import { fetchCustomFeatures } from "src/redux/thunks/custom-cohorts/customFeatureThunk";

/* Constants */
import { INITIAL_INSIGHTS_FILTER_STATE } from "src/shared/constants/insights-filter/InitialState";
import { CustomFeature } from "src/shared/constants/common/CustomFeature";

/* Interfaces */
import {
  InsightsFilterProps,
  FilterType,
  TimePeriodProps,
  TimePeriodType,
} from "src/shared/interfaces/insights-filter/InsightsFilter";
import {
  AgentAnalysis,
  AgentSpec,
} from "src/shared/interfaces/api/AgentSpec";

export interface InsightsFilterState {
  insightsFilter: InsightsFilterProps;
}

/**
 * The time period is the one filter a late options fetch can clobber:
 * it always holds a value, so it has no "unset" state to fall back on
 * the way the cohort does. These two helpers record whether the
 * current value was chosen deliberately — see the
 * `fetchInsightsFilter` reducer below.
 */
const markTimePeriodExplicitlySet = (
  state: InsightsFilterProps
): void => {
  if (!state.timePeriod) return;
  state.timePeriod.isExplicitlySet = true;
};

const clearTimePeriodChoice = (
  state: InsightsFilterProps
): void => {
  if (!state.timePeriod) return;
  state.timePeriod.isExplicitlySet = false;
};

/**
 * Whether a value from an agent spec is one this control can offer.
 *
 * An empty list means the frontend has not loaded those options yet, so
 * it has nothing to judge against and defers to the agent, which
 * validates its choices against the live options itself. Treating
 * "unknown" as "invalid" would drop valid values during the first
 * seconds after a page load.
 */
const isSelectable = (
  value: string,
  items?: string[] | CustomFeature[]
): boolean =>
  !items?.length || (items as string[]).includes(value);

export const insightsFilterSlice = createSlice({
  name: "insightsFilter",
  initialState: INITIAL_INSIGHTS_FILTER_STATE,
  reducers: {
    /**
     *
     * @param {InsightsFilterProps} state - The current state.
     * @param action {PayloadAction<{ key: FilterType; value: string | TimePeriodProps | undefined; }>} action - The action to set the filter value.
     * @returns {void}
     */
    setInsightsFilterValue: (
      state: InsightsFilterProps,
      action: PayloadAction<{
        key: FilterType;
        value: string | TimePeriodProps | null | undefined;
      }>
    ): void => {
      const { key, value } = action.payload;
      const typedKey = key as keyof InsightsFilterProps;
      if (!state[typedKey]) return;

      state[typedKey].value = value;

      if (typedKey === FilterType.TIME_PERIOD) {
        markTimePeriodExplicitlySet(state);
      }
      if (typedKey === FilterType.MAIN_CONDITION) {
        clearTimePeriodChoice(state);
      }
    },

    /**
     * Disable filter by key.
     *
     * @param {InsightsFilterProps} state - The current state.
     * @param {PayloadAction<FilterType>} action - The key to disable the filter.
     * @returns {void}
     */
    disableFilter: (
      state: InsightsFilterProps,
      action: PayloadAction<FilterType>
    ): void => {
      const key = action.payload;
      if (!state[key]) return;

      state[key].disabled = true;
      state[key].show = false;
    },

    /**
     * Clear filter restriction by key.
     *
     * @param {InsightsFilterProps} state - The current state.
     * @param {PayloadAction<FilterType>} action - The key to clear the filter restriction.
     * @returns {void}
     */
    clearFilterRestriction: (
      state: InsightsFilterProps,
      action: PayloadAction<FilterType>
    ): void => {
      const key = action.payload;
      if (!state[key]) return;

      state[key].disabled = false;
      state[key].show = true;
    },

    toggleTimePeriodSelection: (
      state: InsightsFilterProps,
      action: PayloadAction<TimePeriodType>
    ) => {
      const key = action.payload;

      if (!state.timePeriod) return;

      const currentValue = state.timePeriod.value?.[key];
      const previousValue =
        state.timePeriod.previousValue?.[key];
      const initialValue =
        state.timePeriod.initialValue?.[key];

      state.timePeriod.previousValue = {
        ...state.timePeriod.previousValue,
        [key]: currentValue,
      };

      if (currentValue) {
        state.timePeriod.value = {
          ...state.timePeriod.value,
          [key]: undefined,
        };
      } else {
        state.timePeriod.value = {
          ...state.timePeriod.value,
          [key]: previousValue ?? initialValue ?? "",
        };
      }

      markTimePeriodExplicitlySet(state);
    },

    /**
     * Initialise the care site filter items and value from the
     * list of care sites the user is permitted to access.
     */
    initCareSites: (
      state: InsightsFilterProps,
      action: PayloadAction<string[]>
    ): void => {
      if (!state.careSites) return;
      const allowedSites = action.payload;
      const currentSelection = state.careSites.value ?? allowedSites;
      const nextSelection = (currentSelection as string[]).filter(
        (site) => allowedSites.includes(site)
      );
      state.careSites.items = allowedSites;
      state.careSites.initialValue = allowedSites;
      state.careSites.value =
        nextSelection.length > 0 ? nextSelection : allowedSites;
    },

    /**
     * Toggle a single care site in/out of the active selection.
     * The last selected site cannot be deselected.
     */
    toggleCareSite: (
      state: InsightsFilterProps,
      action: PayloadAction<string>
    ): void => {
      if (!state.careSites) return;
      const current = state.careSites.value ?? [];
      const allowed = state.careSites.items ?? [];
      const site = action.payload;

      if (!allowed.includes(site)) return;

      if (current.includes(site)) {
        if (current.length === 1) return;
        state.careSites.value = current.filter(
          (s) => s !== site
        );
      } else {
        state.careSites.value = [...current, site];
      }
    },

    /**
     * Apply an agent-generated configuration spec.
     *
     * Deliberately one action for the whole patch:
     * `useFetchWithMainCondition` watches the entire
     * `insightsFilter` object, so a single dispatch makes
     * "one fetch per applied spec" a property of this code
     * rather than of React's batching.
     *
     * Only fields the spec actually sets are touched — `null`
     * and absent both mean "leave this control unchanged".
     *
     * @param {InsightsFilterProps} state - The current state.
     * @param action - The spec and the care sites the user may use.
     * @returns {void}
     */
    applyAgentSpec: (
      state: InsightsFilterProps,
      action: PayloadAction<{
        spec: AgentSpec;
        allowedCareSites: string[] | null;
      }>
    ): void => {
      const { spec, allowedCareSites } = action.payload;

      /**
       * Values are checked against the loaded options so a stale or
       * mistaken spec cannot put the controls into a selection the
       * deployment does not have.
       *
       * Features and cohorts are the exception: they are enumerated
       * per main condition, so once a spec changes the condition the
       * loaded lists belong to the *previous* one and would reject
       * values that are perfectly valid for the new condition.
       */
      const previousCondition = state.mainCondition.value;

      if (
        spec.main_condition != null &&
        isSelectable(
          spec.main_condition,
          state.mainCondition.items
        )
      ) {
        state.mainCondition.value = spec.main_condition;
        clearTimePeriodChoice(state);
      }

      const conditionChanged =
        state.mainCondition.value !== previousCondition;

      if (
        spec.cohort != null &&
        state.cohorts &&
        (conditionChanged ||
          isSelectable(spec.cohort, state.cohorts.items))
      ) {
        state.cohorts.value = spec.cohort;
      }

      if (
        spec.analysis ===
          AgentAnalysis.DESCRIPTIVE_STATISTICS &&
        spec.feature != null &&
        state.features &&
        (conditionChanged ||
          isSelectable(spec.feature, state.features.items))
      ) {
        state.features.value = spec.feature;
      }

      if (
        spec.analysis === AgentAnalysis.TREATMENT_PATTERNS &&
        spec.custom_feature != null &&
        state.customFeature &&
        isSelectable(
          spec.custom_feature,
          state.customFeature.items
        )
      ) {
        state.customFeature.value =
          spec.custom_feature as CustomFeature;
      }

      if (
        state.timePeriod &&
        (spec.first_year != null || spec.last_year != null)
      ) {
        /**
         * A single bound is completed from the current selection — but
         * only while the main condition stays the same. Across a
         * condition change the other bound belongs to the *previous*
         * condition's data, and marking that mixture as an explicit
         * choice would stop the new condition's range from ever
         * correcting it. Leaving the period alone instead lets the
         * options fetch set the new condition's own range.
         */
        const bothBoundsGiven =
          spec.first_year != null && spec.last_year != null;

        if (!conditionChanged || bothBoundsGiven) {
          const firstYear =
            spec.first_year != null
              ? String(spec.first_year)
              : state.timePeriod.value?.firstYear;
          const lastYear =
            spec.last_year != null
              ? String(spec.last_year)
              : state.timePeriod.value?.lastYear;

          /**
           * An inverted pair is wrong however it arose, and needs no
           * knowledge of the available range to reject. Silently
           * swapping the bounds would invent an intent the prompt
           * never expressed.
           */
          const isInverted =
            firstYear != null &&
            lastYear != null &&
            Number(firstYear) > Number(lastYear);

          if (!isInverted) {
            state.timePeriod.value = { firstYear, lastYear };
            markTimePeriodExplicitlySet(state);
          }
        }
      }

      /**
       * A care site the user cannot access would be rejected by
       * the backend, so it is dropped here; the panel reports
       * what could not be applied.
       */
      if (spec.care_sites?.length && state.careSites) {
        const items = state.careSites.items;
        const permitted = spec.care_sites.filter(
          (site) =>
            (!allowedCareSites ||
              allowedCareSites.includes(site)) &&
            isSelectable(site, items)
        );

        if (permitted.length) {
          state.careSites.value = permitted;
        }
      }
    },

    /**
     * Restore the snapshot taken before an agent spec was
     * applied, so applying a spec is undoable.
     *
     * @param {InsightsFilterProps} _state - The current state.
     * @param {PayloadAction<InsightsFilterProps>} action - The snapshot.
     * @returns {InsightsFilterProps}
     */
    restoreInsightsFilter: (
      _state: InsightsFilterProps,
      action: PayloadAction<InsightsFilterProps>
    ): InsightsFilterProps => action.payload,

    /**
     * Reset all insights filter except `mainCondition`
     * to their initial values.
     *
     * @param {InsightsFilterProps} state - The current state.
     * @returns {void}
     */
    resetInsightsFilter: (
      state: InsightsFilterProps
    ): void => {
      const filterKeys: (keyof InsightsFilterProps)[] = [
        "features",
        "cohorts",
        "customFeature",
        "timePeriod",
        "careSites",
      ];

      filterKeys.forEach((key) => {
        const filter = state[key];
        if (!filter) return;

        filter.value = filter.initialValue;
      });

      clearTimePeriodChoice(state);
    },
  },
  extraReducers: (builder) => {
    /**
     * Fetch main conditions.
     *
     * 'mainConditions'
     *
     * @param {InsightsFilterProps} state - The current state.
     * @param {PayloadAction<string[] | undefined>} action - List of main conditions.
     * @returns {void}
     */
    builder.addCase(
      fetchMainConditions.fulfilled,
      (
        state,
        action: PayloadAction<string[] | undefined>
      ) => {
        const items = action.payload ?? [];
        state.mainCondition.items = items;

        const prevValue = state.mainCondition.value;

        if (items.length === 0) {
          state.mainCondition.value = null;
          state.mainCondition.initialValue = null;
          state.mainCondition.isLoading = false;
          return;
        }

        const isValid =
          prevValue !== undefined &&
          prevValue !== null &&
          items.includes(prevValue as string);

        const firstItem = items[0];

        state.mainCondition.value = isValid
          ? prevValue
          : firstItem;
        state.mainCondition.initialValue = firstItem;
        state.mainCondition.isLoading = false;

        if (!isValid) {
          clearTimePeriodChoice(state);
        }
      }
    );

    builder.addCase(
      fetchMainConditions.pending,
      (state) => {
        state.mainCondition.isLoading = true;
      }
    );

    /**
     * Fetch insights filter.
     *
     * @param {InsightsFilterProps} state - The current state.
     * @param {PayloadAction<TransformedInsightsFilterResponse>} action - List of insights filters.
     * @returns {void}
     */
    builder.addCase(
      fetchInsightsFilter.fulfilled,
      (state, action: PayloadAction<any | undefined>) => {
        if (!action.payload) return;

        const { features, cohorts, firstYear, lastYear } =
          action.payload;

        if (state.features) {
          state.features.items = features;
        }

        if (state.cohorts) {
          state.cohorts.items = cohorts;
          state.cohorts.initialValue = cohorts[0];
          state.cohorts.value =
            state.cohorts.value ?? cohorts[0];
        }

        if (state.timePeriod) {
          if (firstYear && lastYear) {
            const range = { firstYear, lastYear };
            state.timePeriod.initialValue = range;
            /**
             * This fetch can resolve after the user (or an applied
             * agent spec) has already chosen a period — it is the
             * slowest call on the page. Overwriting an explicit
             * choice then would silently show a different period
             * than the one that was asked for, so the range is only
             * adopted while no choice has been made.
             *
             * The flag is cleared whenever the main condition
             * changes, so a different condition still resets the
             * period to its own range.
             */
            if (!state.timePeriod.isExplicitlySet) {
              state.timePeriod.value = range;
            }
          } else {
            state.timePeriod.initialValue = undefined;
            state.timePeriod.value = undefined;
          }
        }
      }
    );

    /**
     * Fetch custom features.
     *
     * @param {InsightsFilterProps} state.
     * @param {PayloadAction<CustomFeatures[] | undefined>} action.
     * @returns {void}
     */
    builder.addCase(
      fetchCustomFeatures.fulfilled,
      (
        state,
        action: PayloadAction<CustomFeature[] | undefined>
      ): void => {
        if (!state.customFeature) return;

        state.customFeature.items = action.payload;

        // If value is `undefined`, set it to the initial value `CustomFeature.TREATMENT`.
        if (!state.customFeature.value) {
          state.customFeature.value =
            state.customFeature.initialValue;
        }
      }
    );
  },
});

/* Actions */
export const {
  setInsightsFilterValue,
  disableFilter,
  clearFilterRestriction,
  toggleTimePeriodSelection,
  resetInsightsFilter,
  initCareSites,
  toggleCareSite,
  applyAgentSpec,
  restoreInsightsFilter,
} = insightsFilterSlice.actions;

/* Reducer */
export default insightsFilterSlice.reducer;
